import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AdditionalStep } from '../../../../../../core/models/additional-step.model';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { Template } from '../../../../../../core/lists/_models/template.model';
import { AdditionalStepsService } from '../../../../../../core/services/additional-steps.service';
import { TemplateEditComponent } from '../template-edit/template-edit.component';
import { TemplateService } from '../../../../../../core/lists/_services/template.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { LayoutUtilsService, MessageType } from '../../../../../../core/_base/crud';
import { EntitiesDialogComponent } from '../entities-dialog/entities-dialog.component';
import { AllowedCompleters } from '../../../../../../core/models/allowed-completers.model';

interface FormValue {
  order: number;
  task_template: Partial<Template>;
  sequential_order: boolean;
  is_externally_completable: boolean;
  allowed_completers: AllowedCompleters[];
}

@Component({
  selector: 'kt-additional-steps',
  templateUrl: './additional-steps.component.html',
  styleUrls: ['./additional-steps.component.scss'],
})
export class AdditionalStepsComponent implements OnInit {
  @Output() emittedAdditionalStep = new EventEmitter<any>();
  @Output() emittedCanAddAdditionalStep = new EventEmitter<any>();
  form: FormGroup;
  index = '';
  toUpdateStep;
  additionalSteps = [];
  @Input() mode;
  templateAutocompleteCall;
  @ViewChild('templateAutocomplete') templateAutocomplete: GeneralAutocompleteComponent;
  canSetAdditionalSteps;
  _templateId: number;
  @Input() set templateId(value: number) {
    if (!value) {
      this.additionalSteps = [];
      return;
    }
    this._templateId = value;
    this.templateAutocompleteCall = this.additionalStepsService.searchAdditionalSteps.bind(
      this.additionalStepsService,
      this._templateId,
    );
    this.loadAdditionalSteps(value);
  }
  @Input() lockedByAdmin;
  constructor(
    private cdr: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private additionalStepsService: AdditionalStepsService,
    private templateService: TemplateService,
    private layoutUtilsService: LayoutUtilsService,
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      task_template: [null, Validators.required],
      sequential_order: [false],
      is_externally_completable: [false],
      allowed_completers: [[]],
    });
    if (this.lockedByAdmin) this.mode = 'view';
    // View Mode
    if (this.mode === 'view') this.form.disable();
  }

  reset(): void {
    this.index = '';

    this.form.reset();
    this.templateAutocomplete?.setValue(null);
    this.templateAutocomplete.setErrors(null);
  }
  onCompletableChange(): void {
    const currentAllowedCompleters =
      this.form.get('allowed_completers')?.value || [];

    const dialogRef = this.dialog.open(EntitiesDialogComponent, {
      width: '600px',
      height: '500px',
      data: {
        allowed_completers: currentAllowedCompleters,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      const allowedCompleters = result.allowed_completers || [];

      this.form.patchValue({
        is_externally_completable: allowedCompleters.length > 0,
        allowed_completers: allowedCompleters,
      });

      this.cdr.detectChanges();
    });
  }
  mapFormToAdditionalStep(value: FormValue) {
    return {
      order: value.order || this.additionalSteps.length + 1,
      task_template_id: value.task_template?.id,
      sequential_order: value.sequential_order || false,
      is_externally_completable: value.is_externally_completable || false,
      allowed_completers: value.allowed_completers || [],
    };
  }

  mapAdditionalStepToForm(step: Partial<AdditionalStep>): FormValue {
    return {
      order: step.order,
      task_template: step.task_template,
      sequential_order: step.sequential_order || false,
      is_externally_completable: step.is_externally_completable || false,
      allowed_completers: step.allowed_completers || [],
    };
  }

  addStep(event: Event): void {
    event.stopPropagation();
    const formValue = this.form.value;
    if (!formValue.task_template) {
      return;
    }
    if (this.index) {
      this.additionalStepsService
        .updateAdditionalSteps(this.mapFormToAdditionalStep(formValue), this.toUpdateStep?.id)
        .subscribe(() => {
          this.loadAdditionalSteps(this._templateId);
          this.layoutUtilsService.showActionNotification(
            'Additional steps updated successfully',
            MessageType.Update,
            3000,
            false,
            false,
          );
        });
    } else {
      this.additionalStepsService
        .addAdditionalSteps(this.mapFormToAdditionalStep(formValue), this._templateId)
        .subscribe(() => {
          this.loadAdditionalSteps(this._templateId);
          this.layoutUtilsService.showActionNotification(
            'Additional steps added successfully',
            MessageType.Create,
            3000,
            false,
            false,
          );
        });
    }
    this.reset();
  }

  updateStep(event: Event, idx: number, step): void {
    event.stopPropagation();

    const additionalStep = this.additionalSteps[idx];

    this.index = `${idx}`;
    this.toUpdateStep = step;
    const formValue = this.mapAdditionalStepToForm(additionalStep);

    this.form.patchValue({
      task_template: formValue.task_template,
      sequential_order: formValue.sequential_order,
      is_externally_completable: formValue.is_externally_completable,
      allowed_completers: formValue.allowed_completers || [],
    });

    this.templateAutocomplete.setValue(formValue.task_template);
  }

  cancelStepUpdate(event: Event): void {
    event.stopPropagation();

    this.reset();
  }

  deleteStep(event: Event, step): void {
    event.stopPropagation();
    const _title = 'Delete Additional Step';
    const _description = 'Are you sure you want to delete this additional step?';
    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, 'Loading...');
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.additionalStepsService.deleteAdditionalStep(step.id).subscribe(() => {
        this.loadAdditionalSteps(this._templateId);
      });
    });
  }

  filterTemplateFn(template: any): string {
    return template ? template.name : '';
  }

  onTemplateChange(val) {
    this.form.get('task_template').setValue(val);
    this.form.get('task_template').updateValueAndValidity();
  }

  addNewTemplate() {
    const dialogRef = this.templateService.openDialog(TemplateEditComponent, {
      width: '97vw',
      data: { template: new Template(), isReadOnly: false, mode: 'additional_step' },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      if (result.template) {
        this.templateAutocomplete.setValue(result.template);
        this.form.get('task_template').setValue(result.template);
        this.form.get('task_template').updateValueAndValidity();
      }
    });
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.additionalSteps, event.previousIndex, event.currentIndex);
    this.additionalSteps = this.additionalSteps.map((step, index) => {
      return {
        order: index + 1,
        sequential_order: step?.sequential_order || false,
        task_template: step?.task_template,
        id: step?.id,
      };
    });
    const updatedOrder = this.additionalSteps.map((step) => {
      return {
        id: step?.id,
        order: step?.order,
        sequential_order: step?.sequential_order,
      };
    });
    this.additionalStepsService.updateOrderAdditionalStep(updatedOrder).subscribe();
  }
  loadAdditionalSteps(templateID) {
    this.additionalStepsService.getAdditionalSteps(templateID).subscribe((additionalSteps) => {
      this.additionalSteps = additionalSteps?.data?.additional_step_template_dtos || [];
      this.additionalSteps = this.additionalSteps.sort((a, b) => a.order - b.order);
      this.canSetAdditionalSteps = additionalSteps?.data?.can_add_additional_steps;
      this.emittedAdditionalStep.emit(this.additionalSteps);
      this.emittedCanAddAdditionalStep.emit(this.canSetAdditionalSteps);
      this.cdr.detectChanges();
    });
  }
}
